/**
 * At-rest encryption primitives. ADR-0006, S-04, T-04.
 *
 * ```
 *   PIN / passphrase
 *         │  PBKDF2-HMAC-SHA-256, 600,000 iterations, 16-byte random salt
 *         ▼
 *   KEK (AES-GCM 256)  ── held in memory only, dropped on lock
 *         │  wraps
 *         ▼
 *   DEK (AES-GCM 256, random, generated once at install)
 *         │  encrypts
 *         ▼
 *   every record value in IndexedDB
 * ```
 *
 * ## Why two keys
 *
 * Changing a PIN on a ten-year history must not mean re-encrypting ten years of
 * records: a half-completed re-encryption of a health record is a data-loss bug
 * waiting to happen. With wrapping, a PIN change rewraps one key and touches
 * nothing else.
 *
 * ## What this does NOT protect against
 *
 * A 4–6 digit PIN is guessable offline by anyone holding the database. 600,000
 * iterations raises the cost of that attack; it does not make it hard. The
 * honest framing is in the README's limitations list, and the UI offers a
 * passphrase and explains the difference. Pretending otherwise would be exactly
 * the overclaim the FTC pursues.
 *
 * Everything here uses WebCrypto. No dependency is added inside the crypto
 * boundary, which is where a compromised dependency does the most damage
 * (T-14).
 */

/** OWASP guidance for PBKDF2-HMAC-SHA-256. Stored per keyring so it can rise. */
export const PBKDF2_ITERATIONS = 600_000

/**
 * The count used when creating a NEW keyring or export.
 *
 * Overridable only so that integration tests — which set and unlock a PIN many
 * times — do not each pay a second of derivation. The production value is
 * exercised for real by `crypto.test.ts`, deliberately and once: a suite that
 * only ever ran a cheap count would not know whether the shipped parameter
 * works on the target device.
 *
 * Reading and unlocking an EXISTING keyring always uses the count stored with
 * it, never this — otherwise raising the constant would lock users out.
 */
let activeIterations = PBKDF2_ITERATIONS

export const currentIterations = (): number => activeIterations

/** Test seam. Not used by application code. */
export function setIterationsForTests(iterations: number): void {
  activeIterations = iterations
}

const SALT_BYTES = 16
const IV_BYTES = 12 // AES-GCM standard; 96 bits is the size GCM is defined for.
const KEY_BITS = 256

export interface SealedBlob {
  /** Initialisation vector, base64. Fresh for every single encryption. */
  iv: string
  /** Ciphertext with the GCM tag appended, base64. */
  ct: string
}

export const toBase64 = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return bytes
}

export const newSalt = (): Uint8Array => randomBytes(SALT_BYTES)

/** A fresh random data-encryption key. Generated once per install. */
export function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: KEY_BITS }, true, [
    'encrypt',
    'decrypt',
  ])
}

/**
 * Derive the key-encryption key from a PIN or passphrase.
 *
 * Deliberately slow. This is the only thing standing between a stolen database
 * and its contents, so the iteration count is the security parameter and it is
 * stored alongside the keyring rather than hardcoded at the call site — an
 * existing keyring must keep verifying against the count it was created with,
 * even after the constant above is raised.
 */
export async function deriveKek(
  secret: string,
  salt: Uint8Array,
  iterations: number = activeIterations,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    'PBKDF2',
    false,
    ['deriveKey'],
  )

  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['wrapKey', 'unwrapKey'],
  )
}

/** Wrap the DEK under the KEK so it can be stored. */
export async function wrapDek(dek: CryptoKey, kek: CryptoKey): Promise<SealedBlob> {
  const iv = randomBytes(IV_BYTES)
  const wrapped = await crypto.subtle.wrapKey('raw', dek, kek, {
    name: 'AES-GCM',
    iv: iv as unknown as BufferSource,
  })
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(wrapped)) }
}

/**
 * Recover the DEK.
 *
 * Throws when the secret is wrong. That is not incidental — AES-GCM
 * authenticates, so a wrong KEK fails to unwrap rather than producing a
 * plausible-looking wrong key. There is no separate "is this PIN correct?"
 * check to get wrong, and no verifier value to leak.
 */
export function unwrapDek(blob: SealedBlob, kek: CryptoKey): Promise<CryptoKey> {
  return crypto.subtle.unwrapKey(
    'raw',
    fromBase64(blob.ct) as unknown as BufferSource,
    kek,
    { name: 'AES-GCM', iv: fromBase64(blob.iv) as unknown as BufferSource },
    { name: 'AES-GCM', length: KEY_BITS },
    true,
    ['encrypt', 'decrypt'],
  )
}

/** Export the DEK for storage when no PIN is set. See `keyring.ts` for why. */
export async function exportDek(dek: CryptoKey): Promise<string> {
  return toBase64(new Uint8Array(await crypto.subtle.exportKey('raw', dek)))
}

export function importDek(raw: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    fromBase64(raw) as unknown as BufferSource,
    { name: 'AES-GCM', length: KEY_BITS },
    true,
    ['encrypt', 'decrypt'],
  )
}

/**
 * Seal a value under the DEK.
 *
 * A fresh IV every time, without exception. Reusing an IV with the same key in
 * GCM is catastrophic — it leaks the XOR of two plaintexts and can expose the
 * authentication key. Since one DEK encrypts every record for the life of an
 * install, this is the invariant that matters most in this file, and it is
 * tested directly.
 */
export async function seal(value: unknown, dek: CryptoKey): Promise<SealedBlob> {
  const iv = randomBytes(IV_BYTES)
  const plaintext = new TextEncoder().encode(JSON.stringify(value))
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    dek,
    plaintext,
  )
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(ct)) }
}

/**
 * Open a sealed value.
 *
 * Throws on a wrong key OR on tampered ciphertext — GCM verifies its tag before
 * returning anything, so there is no path where modified data is silently
 * accepted.
 */
export async function open<T>(blob: SealedBlob, dek: CryptoKey): Promise<T> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(blob.iv) as unknown as BufferSource },
    dek,
    fromBase64(blob.ct) as unknown as BufferSource,
  )
  return JSON.parse(new TextDecoder().decode(plaintext)) as T
}

/**
 * A rough strength signal for the PIN entry screen.
 *
 * Not a security control — it informs the user, it does not gate them. The
 * numbers are deliberately conservative and the UI must explain what the
 * limitation actually is (T-10) rather than showing a green bar and implying
 * safety.
 */
export function secretStrength(secret: string): 'weak' | 'fair' | 'strong' {
  const digitsOnly = /^\d+$/.test(secret)
  if (digitsOnly) return secret.length >= 8 ? 'fair' : 'weak'
  if (secret.length >= 16) return 'strong'
  if (secret.length >= 10) return 'fair'
  return 'weak'
}
